import React from 'react';

import { urlFor } from '../lib/sanity';

/**
 * Shared layout for team pages (Trainers, Game Masters).
 * Renders a photo grid of people and a detail lightbox with name + description.
 */
function TeamSection({
  titlePlain,
  titleAccent,
  subtitle,
  fetchMembers,
  loadingLabel = 'Loading...',
  emptyMessage = 'Profiles are coming soon.',
}) {
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // fetch data from Sanity
  React.useEffect(() => {
    fetchMembers()
      .then((data) => {
        setMembers(data || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching team members:', error);
        setLoading(false);
      });
  }, [fetchMembers]);

  // urlFor returns null when the image is missing, so guard before chaining
  const imageUrl = (source, width) => {
    const builder = urlFor(source);
    return builder ? builder.width(width).auto('format').url() : null;
  };

  // Swipping logic
  const touchStartX = React.useRef(null);
  const touchEndX = React.useRef(null);
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!members.length) return;
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) < minSwipeDistance) return;

    if (distance > 0) {
      // Swiped left → next
      setSelectedIndex((prev) => (prev === members.length - 1 ? 0 : prev + 1));
    } else {
      // Swiped right → previous
      setSelectedIndex((prev) => (prev === 0 ? members.length - 1 : prev - 1));
    }
  };

  const closeModal = () => setSelectedIndex(null);

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? members.length - 1 : prev - 1));
  };

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev === members.length - 1 ? 0 : prev + 1));
  };

  React.useEffect(() => {
    if (selectedIndex === null) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft') prevImage(e);
      if (e.key === 'ArrowRight') nextImage(e);
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, members.length]);

  const selected = selectedIndex !== null ? members[selectedIndex] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute top-10 left-10 w-96 h-96 bg-[#FED700]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#F4900C]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {titlePlain} <span className="text-[#F4900C]">{titleAccent}</span>
            </h1>

            <p className="text-xl text-gray-600">{subtitle}</p>
          </div>
        </div>
      </section>

      {/* Members Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#F4900C] mx-auto mb-4"></div>
              <p className="text-gray-600">{loadingLabel}</p>
            </div>
          ) : members.length === 0 ? (
            <p className="text-center text-gray-600 py-12">{emptyMessage}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {members.map((member, index) => (
                <div
                  key={member._id}
                  onClick={() => setSelectedIndex(index)}
                  className="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-zoom-in flex flex-col"
                >
                  {/* Photo */}
                  <div className="relative h-56 sm:h-64 overflow-hidden bg-gray-100">
                    {imageUrl(member.image, 400) ? (
                      <img
                        src={imageUrl(member.image, 400)}
                        alt={member.name}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                        No photo
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>

                  {/* Info — omitted entirely for records that have no text yet */}
                  {(member.name || member.shortDescription) && (
                    <div className="p-4 flex-grow">
                      {member.name && (
                        <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#F4900C] transition-colors">
                          {member.name}
                        </h3>
                      )}
                      {member.shortDescription && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {member.shortDescription}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 sm:p-8"
          onClick={closeModal}
        >
          {/* Close Button */}
          <button
            onClick={closeModal}
            aria-label="Close"
            className="absolute top-4 right-6 text-white text-4xl hover:text-gray-300 z-10"
          >
            ×
          </button>

          {/* Left Arrow */}
          {members.length > 1 && (
            <button
              onClick={prevImage}
              aria-label="Previous"
              className="absolute left-2 sm:left-6 text-white text-5xl hover:scale-110 transition-transform z-10"
            >
              ‹
            </button>
          )}

          <div
            className="relative bg-white rounded-2xl overflow-hidden shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col md:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Photo */}
            <div className="md:w-1/2 bg-gray-100 flex items-center justify-center flex-shrink-0">
              {imageUrl(selected.image, 1000) ? (
                <img
                  src={imageUrl(selected.image, 1000)}
                  alt={selected.name}
                  className="w-full max-h-[40vh] md:max-h-[85vh] object-contain select-none touch-pan-y"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                />
              ) : (
                <div className="w-full h-48 flex items-center justify-center text-gray-400">
                  No photo
                </div>
              )}
            </div>

            {/* Details */}
            {(selected.name || selected.shortDescription) && (
              <div className="md:w-1/2 p-6 sm:p-8 overflow-y-auto">
                {selected.name && (
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                    {selected.name}
                  </h2>
                )}
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {selected.shortDescription}
                </p>
              </div>
            )}
          </div>

          {/* Right Arrow */}
          {members.length > 1 && (
            <button
              onClick={nextImage}
              aria-label="Next"
              className="absolute right-2 sm:right-6 text-white text-5xl hover:scale-110 transition-transform z-10"
            >
              ›
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TeamSection;
